import React, { useEffect, useState, useRef } from 'react';

interface CircularCountdownProps {
    initialSeconds: number;
    onComplete?: () => void;
}

export const CircularCountdown: React.FC<CircularCountdownProps> = ({
    initialSeconds,
    onComplete
}) => {
    const [timeLeft, setTimeLeft] = useState(initialSeconds * 1000);
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    // Circle properties
    const size = 28;
    const strokeWidth = 3;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    // Calculate progress (0 to 1)
    const progress = timeLeft / (initialSeconds * 1000);
    const offset = circumference * (1 - progress);
    const seconds = Math.ceil(timeLeft / 1000);

    useEffect(() => {
        const startTime = Date.now();
        const endTime = startTime + initialSeconds * 1000;
        let rafId: number;

        const animate = () => {
            const now = Date.now();
            const remaining = Math.max(0, endTime - now);

            setTimeLeft(remaining);

            if (remaining > 0) {
                rafId = requestAnimationFrame(animate);
            } else {
                onCompleteRef.current?.();
            }
        };

        rafId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, [initialSeconds]);

    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Background circle (gray) */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#4a4a4a"
                    strokeWidth={strokeWidth}
                />

                {/* Progress circle (green) */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    className="stroke-success-500"
                    fill="none"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(0 ${size / 2} ${size / 2})`}
                />
            </svg>

            {/* Centered number */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.6rem',
                fontWeight: 'bold',
                color: 'white',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                {seconds}
            </div>
        </div>
    );
};